<script setup>
import { computed, onBeforeUnmount, onMounted, onUnmounted, ref } from "vue";
import Spinner from "@/components/Spinner.vue"

import { useGrid, useScreen } from 'vue-screen'
import DevicePreview from "@/components/DevicePreview.vue";
import HistorySection from "@/views/preview/sections/HistorySection.vue";
import HighlightBox from "@/views/preview/partials/HighlightBox.vue";
import ProofreadingSection from "@/views/preview/sections/ProofreadingSection.vue";
import AiReadinessSection from "@/views/preview/sections/AiReadinessSection.vue";
import CrossReferenceSection from "@/views/preview/sections/CrossReferenceSection.vue";
import QASection from "@/views/preview/sections/QASection.vue";
import OverviewSection from "@/views/preview/sections/OverviewSection.vue";
import AuditSection from "@/views/preview/sections/AuditSection.vue";
import RadialProgress from "vue3-radial-progress";
import { getProgressColor } from "@/utils";
import SectionSwitcher from "@/views/preview/SectionSwitcher.vue";
import { usePreviewLogic } from "@/composables/usePreviewLogic";
import TabbedSectionSwitcher from "@/views/preview/TabbedSectionSwitcher.vue";
import HeatMap from "@/components/HeatMap.vue";
import { observeInlineFaIcons } from '@/utils/inlineFaIcons';
import { storeToRefs } from "pinia";
import { useIssuesStore } from "@/stores/issues";
import { EventBus } from "@/events/eventBus";

const issuesStore = useIssuesStore();

const screen = useScreen()
const grid = useGrid('bootstrap5')
const previewRoot = ref(null)
let stopInlineFaObserver = () => {
}

const {
  issueFilter,
  devicePreview,
  highlightBox,
  highlightDetails,
  historyPreview,
  filteredIssues,
  showBoxes,
  onLoaded,
  scroll,
  onManualScroll,
  onScrollToEntryCompleted,
  logClick,
  activeDeviceRender,
  route,
} = usePreviewLogic();

const maxWidth = computed(() => {
  if (grid.xl) {
    return 600;
  }
  if (grid.lg) {
    return 368;
  }
  return Math.min(screen.width - 100, 350)
})
const history = computed(() => {

  let result = [];
  for (let entry of activeDeviceRender.page.history) {
    if (!entry.device_renders) {
      continue;
    }
    let r = entry.device_renders.find((item) => {
      return item.device === activeDeviceRender.deviceRender.deviceId
    })
    if (r) {
      let h = {}
      Object.assign(h, r);
      h.pageId = entry.id;
      h.trackedPrompts = entry.tracked_prompts;
      result.push(h)
    }
  }
  result.reverse()
  return result;
})

const selectedDeviceId = computed(() => activeDeviceRender.deviceRender?.deviceId)

function deviceIconClass(deviceId) {
  return ['device-icon', {'device-icon-selected': selectedDeviceId.value === deviceId}]
}

async function sendResults() {
  if (!activeDeviceRender.loaded){
    return
  }

  const {
    brokenLinkMarks,
    internalRedirectMarks,
    brokenImageMarks,
    missingAltTagImageMarks,
    brokenResources,
    consoleErrors,
    layoutErrors,
    contentErrorMissingFavicons,
    technicalMissingDescriptions,
    technicalMissingTitles,
  } = storeToRefs(issuesStore);
  const {proofreadIssues} = storeToRefs(activeDeviceRender);

  function formatIssueSection(title, issues) {
    if (issues.length > 0) {
      return `### ${title}\n${issues.join('')}`
    }

    return `### ${title}\n- no issues found`
  }

  let proofreadIssuesContext = "### Proofreading Issues\n";
  if (proofreadIssues.value && (['completed', 'error'].indexOf(proofreadIssues.value.status) !== -1)) {
    for (let issue of proofreadIssues.value.processed_data?.result || []) {
      proofreadIssuesContext += `- ${issue.category} [${issue.severity}]: ${issue.explanation}\n`
    }
  }
  else{
    proofreadIssuesContext += '- Checks in progress..'
  }

  let aiReadinessIssuesContext = "### AI Readiness Issues\n";
  if (proofreadIssues.value && (['completed', 'error'].indexOf(proofreadIssues.value.ai_readiness_status) !== -1)) {
    for (let issue of proofreadIssues.value.processed_ai_readiness?.result || []) {
      aiReadinessIssuesContext += `- ${issue.category} [${issue.severity}]: ${issue.explanation}\n`
    }
  }
  else{
    aiReadinessIssuesContext += '- Checks in progress..'
  }

  let layoutIssues = "";

  for (let issue of layoutErrors.value) {
    let devices = '['
    for (let device of issue.devices){
      devices += `${device.name},`
    }
    devices = devices.substring(0,devices.length-1) + ']'
    layoutIssues += `- ${devices} ${issue.query} [${issue.priority}]: ${issue.description}\n`
  }

  for (let issue of contentErrorMissingFavicons.value) {
    layoutIssues += `- missing-favicon [${issue.priority}]\n`
  }
  for (let issue of technicalMissingDescriptions.value) {
    layoutIssues += `- missing-description [${issue.priority}]:  Missing  <meta name="description" content=".."/> tag\n`
  }
  for (let issue of technicalMissingTitles.value) {
    layoutIssues += `- missing-title [${issue.priority}]: Missing <title>...</title> tag\n`
  }

  layoutIssues = formatIssueSection('Layout Issues', layoutIssues ? [layoutIssues] : [])

  let linkIssues = "";

  for (let issue of brokenLinkMarks.value) {
    linkIssues += `- broken-link [${issue.priority}]: ${issue.description}\n`
  }
  for (let issue of internalRedirectMarks.value) {
    linkIssues += `- internal-redirect [${issue.priority}]: ${issue.description}\n`
  }

  linkIssues = formatIssueSection('Link Issues', linkIssues ? [linkIssues] : [])

  let imageIssues = "";

  for (let issue of brokenImageMarks.value) {
    imageIssues += `- broken-image [${issue.priority}]: ${issue.description}\n`
  }
  for (let issue of missingAltTagImageMarks.value) {
    imageIssues += `- missing-alt-tag [${issue.priority}]: ${issue.description}\n`
  }

  imageIssues = formatIssueSection('Image Issues', imageIssues ? [imageIssues] : [])

  let consoleErrorIssues = "";

  for (let issue of consoleErrors.value) {
    consoleErrorIssues += `- console-error [${issue.priority}]: ${issue.text}\n`
  }

  consoleErrorIssues = formatIssueSection('Console Errors', consoleErrorIssues ? [consoleErrorIssues] : [])

  let resourceIssues = "";

  for (let issue of brokenResources.value) {
    resourceIssues += `- broken-resource [${issue.priority}]: ${issue.type} ${issue.status} ${issue.url}\n`
  }

  resourceIssues = formatIssueSection('Resource Issues', resourceIssues ? [resourceIssues] : [])

  let context = `---
url: ${activeDeviceRender.page?.url}
scan-state: ${activeDeviceRender.page.status}
---

${layoutIssues}

${linkIssues}

${imageIssues}

${consoleErrorIssues}

${resourceIssues}

${proofreadIssuesContext}
${aiReadinessIssuesContext}
`
  console.log(context)

  if (window.mcpApp) {
    await window.mcpApp.updateModelContext({
      structuredContent: { _pw_route: window._pwCurrentRoute },
      content: [
        {
          type: "text",
          text: context
        },
      ],
    });
  }
}


async function showBrowser() {
  console.log('r')
  await window.mcpApp.callServerTool({name: "launch_visible_browser", arguments: {}});
}

const buttonText = ref('hello');
window.addEventListener('mcp:message', (e) => {
  const {type, content} = e.detail;
  buttonText.value = 'xx';
  console.log(e)
  if (type === 'tool-result') {
    // handle the tool result content array
  }
});

onMounted(() => {
  stopInlineFaObserver = observeInlineFaIcons(previewRoot.value)
  EventBus.on('refetchAll', sendResults);
})

onBeforeUnmount(() => {
  stopInlineFaObserver()
  EventBus.off('refetchAll', sendResults);
})
</script>

<template>
  <div ref="previewRoot">
    <highlight-box ref="highlightBox" :highlight-details="highlightDetails"></highlight-box>

    <div v-if="activeDeviceRender.loaded" class="mcp-container mcp-layout-parent" @click="logClick">
      <div class="mcp-layout">
        <!-- Device Preview Column -->
        <div class="mcp-preview-column">
          <div class="mcp-preview-wrapper"
               :class="`preview-${activeDeviceRender.devicePreview.deviceId}`">
            <div class="mcp-device-container">
              <device-preview ref="devicePreview" v-if="activeDeviceRender.loaded" :maxHeight="450"
                              :maxWidth="340"
                              :history="historyPreview"
                              @loaded="onLoaded"
                              :disableScreenshotMain="!!route.query.section"
                              @on-manual-scroll="onManualScroll"
                              @on-scroll-to-entry-completed="onScrollToEntryCompleted"
                              v-bind="activeDeviceRender.devicePreview"></device-preview>
            </div>
          </div>
        </div>

        <!-- Content Column -->
        <div class="mcp-content-column">
          <div class="mcp-content-wrapper">

            <div class="mcp-error-alert" role="alert"
                 v-if="!activeDeviceRender.page.httpStatusOkOrPending">
              <p class="mb-0"><b>Page Load Error</b><br> We could not load this page, the server responded with a status
                code of {{ activeDeviceRender.page.httpStatusCode }}. </p>
            </div>

            <div v-else class="mcp-card">
              <div class="mcp-card-body">
                <!-- Section Switcher -->
                <div class="p-2 d-flex align-items-center" style="max-width: 100%; overflow: hidden;">
                  <router-link :to="{params: {deviceId: 'chrome_1280'}, query: route.query}">
                    <i :class="['fa', 'fa-desktop', 'me-2', ...deviceIconClass('chrome_1280')]"
                       style="font-size: 24px"></i>
                  </router-link>
                  <router-link :to="{params: {deviceId: 'ipad_pro_11'}, query: route.query}">
                    <i :class="['fa-solid', 'fa-tablet-screen-button', 'me-2', ...deviceIconClass('ipad_pro_11')]"
                       style="font-size: 24px"></i>
                  </router-link>
                  <router-link :to="{params: {deviceId: 'iphone_14'}, query: route.query}">
                    <i :class="['fa-solid', 'fa-mobile-screen', 'me-2', ...deviceIconClass('iphone_14')]"
                       style="font-size: 24px"></i>
                  </router-link>
                  <div class="position-relative" style="flex: 1; min-width: 0;">
                    <heat-map :history="history" label=""></heat-map>
                  </div>
                  <div v-if="!['completed', 'error'].includes(activeDeviceRender.scanState)" class="position-relative ms-1" style="top:-8px"><spinner></spinner></div>
                </div>
                <tabbed-section-switcher/>
              </div>

              <!-- Section Content -->
              <div class="mcp-section-content">
                <template v-if="route.query.section === 'proofread'">
                  <proofreading-section :devicePreview="devicePreview" v-if="devicePreview"></proofreading-section>
                </template>
                <template v-if="route.query.section === 'consistency'">
                  <cross-reference-section :devicePreview="devicePreview"
                                           v-if="devicePreview"></cross-reference-section>
                </template>
                <template v-if="route.query.section === 'ai_readiness'">
                  <ai-readiness-section :devicePreview="devicePreview" v-if="devicePreview"></ai-readiness-section>
                </template>
                <template v-if="route.query.section === 'history'">
                  <history-section :devicePreview="devicePreview" v-if="devicePreview" :mcp="true"/>
                </template>
                <template v-if="route.query.section === 'qa'">
                  <q-a-section :devicePreview="devicePreview" v-if="devicePreview"/>
                </template>
                <template v-if="!route.query.section || route.query.section === 'audit'">
                  <audit-section
                      :issueFilter="issueFilter"
                      :mcp="true"
                      @update:issueFilter="issueFilter = $event"
                      :filteredIssues="filteredIssues"
                      :showBoxes="showBoxes"
                  />
                </template>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>

</template>

<style scoped>
/* MCP-specific container styles */
.mcp-container {
  padding: 1rem;
  background: #f8f9fa;
}

.mcp-layout {
  display: flex;
  gap: 1rem;
  max-width: 1400px;
  margin: 0 auto;
}

.mcp-preview-column {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 340px;
}

.mcp-preview-wrapper {
  display: flex;
  gap: 1rem;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.mcp-scroll-controls {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mcp-scroll-btn {
  padding: 0.5rem 0.75rem;
  border: 1px solid #dee2e6;
  background: white;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: all 0.2s;
}

.mcp-scroll-btn:hover {
  background: #e9ecef;
  border-color: #adb5bd;
}

.device-icon {
  color: rgba(108, 117, 125, 0.72);
  transition: color 0.2s ease, transform 0.2s ease;
}

.device-icon::before {
  content: none !important;
  display: none !important;
}

.device-icon :deep(.inline-fa-svg),
.device-icon :deep(.inline-fa-svg path) {
  fill: currentColor;
}

.device-icon :deep(.inline-fa-svg) {
  display: block;
  width: 1em;
  height: 1em;
}

a:hover .device-icon {
  color: #495057;
}

.device-icon-selected {
  color: #0d6efd;
  transform: scale(1.1);
}

.mcp-device-container {
  position: relative;
  white-space: nowrap;
  min-width: 220px;
}

.mcp-content-column {
  flex: 1;
  min-width: 0;
}

.mcp-content-wrapper {
  background: white;
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.mcp-error-alert {
  margin: 1.5rem;
  padding: 1rem;
  background: #f8d7da;
  border: 1px solid #f5c2c7;
  border-radius: 0.375rem;
  color: #842029;
}

.mcp-card {
  border: none;
}

.mcp-card-body {
  padding: 0;
}

.mcp-header {
  display: flex;
  align-items: center;
  gap: 1.0rem;
  padding: 1rem;
  border-bottom: 2px solid #e9ecef;
}

.mcp-score-container {
  flex-shrink: 0;
}

.mcp-page-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.mcp-page-title {
  margin: 0;
  font-size: 0.9rem;
  font-weight: 700;
  color: #212529;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.mcp-page-url {
  display: flex;
  align-items: center;
  color: #6c757d;
  font-size: 0.675rem;
  overflow: hidden;
}

.mcp-section-content {
  background: white;
  border-top: 1px solid #e9ecef;
  max-height: 70vh;
  overflow-y: auto;
}

</style>

<style>
/* Global styles for audit evidence modifiers */
.audit-evidence.audit-evidence-proofread::before {
  content: "PROOFREADING CHECKS";
}

.audit-evidence.audit-evidence-custom::before {
  content: var(--evidence-label, "");
}

.audit-evidence.audit-evidence-cross-page::before {
  content: "PAGE CONSISTENCY CHECKS";
}

.audit-evidence.audit-evidence-history::before {
  content: "PAGE HISTORY";
}

.infoblock .block-content {
  padding: 4px;
  font-size: 94%;
}

.issue-alert {
  background-color: red;
  z-index: 99;
  max-width: 450px;
  white-space: normal;
  color: white;
  padding: 4px;
  border-radius: 8px;
  font-size: 13px;

  span.fa-caret-down {
    color: red;
  }
}

.issue-alert .info {
  color: #1391aa;

  span.fa-caret-down {
    color: #1391aa;
  }
}

.issue-alert .danger {
  color: red;

  span.fa-caret-down {
    color: red;
  }
}

.issue-alert.info {
  background-color: #1391aa;

  span.fa-caret-down {
    color: #1391aa;
  }
}

.issue-alert-thin {
  max-width: 370px;
}

.issue-alert a {
  color: white;
  text-decoration: underline;
}

.issue-alert-code {
  margin-bottom: 0;
  overflow-wrap: break-word;
  margin-top: 4px;
  background-color: #464646;
  font-size: 11px;
  max-height: 54px;
}
</style>
